import React from 'react';
import { AddressTypes, ListActionSelect } from '@sweetgreen/garnish';

import type { DeliveryPreferences } from '@order/components';
import {
  DeliveryAddressListAction,
  DeliveryPreferencesSelector,
} from '@order/components';
import { DeliveryPreferenceType } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import type { AddressForm } from '../../../../../components/Address/AddressForm.types';
import { mapAvailableTimes } from '../../../Checkout.utils';
import { useFirstAvailableOption } from '../../../hooks/useFirstAvailableOption/useFirstAvailableOption';
import { CheckoutBlockVStack } from '../CheckoutBlockVStack';
import type { CheckoutDetailsBlockProps } from './CheckoutDetailsBlock.types';

export const DeliveryOptions = React.memo(
  ({
    orderId,
    orderType,
    orderAddress,
    restaurant,
    deliveryOrderDetail,
    submittingAddress,
    availableWantedTimeOptions,
    loadingAvailableWantedTimes,
    selectedTime,
    handleUpdateAddress,
    setSelectedTime,
  }: CheckoutDetailsBlockProps) => {
    const { t } = useLocalizationContext();

    const timeOptionsGroups = React.useMemo(
      () =>
        mapAvailableTimes({
          t,
          orderType,
          times: availableWantedTimeOptions,
        }),
      [t, orderType, availableWantedTimeOptions],
    );

    useFirstAvailableOption({
      timeOptionsGroups,
      selectedTime,
      setSelectedTime,
    });

    // ──────── Telemetry ─────────
    const { track } = useTelemetry();

    const onTimeChange = React.useCallback(
      (time: string) => {
        const delta =
          new Date(time).getTime() - new Date(selectedTime).getTime();

        track('checkout.time_changed', { timeChanged: delta / (60 * 1000) });

        setSelectedTime(time);
      },
      [selectedTime, setSelectedTime, track],
    );
    // ────────────────────────────

    const onSave = React.useCallback(
      (preferences: DeliveryPreferences) => {
        handleUpdateAddress({
          ...orderAddress,
          deliveryPreference: preferences.type,
          notes: preferences.notes,
        } as AddressForm);
      },
      [orderAddress, handleUpdateAddress],
    );

    if (orderType !== AddressTypes.delivery) return null;

    return (
      <CheckoutBlockVStack testID="checkout-block-delivery">
        <ListActionSelect
          testID="checkout.delivery-time"
          iconName="IconClock"
          title={t('checkout.delivery-time')}
          modalTitle={t('checkout.choose-delivery-time')}
          value={selectedTime}
          onSelect={onTimeChange}
          options={timeOptionsGroups}
          isLoading={loadingAvailableWantedTimes}
        />
        <DeliveryAddressListAction
          orderId={orderId}
          deliveryAddress={orderAddress}
          restaurant={restaurant}
          deliveryOrderDetail={deliveryOrderDetail}
          orderType={orderType}
        />
        <DeliveryPreferencesSelector
          preferences={{
            type:
              orderAddress?.deliveryPreference ??
              DeliveryPreferenceType.LeaveAtDoor,
            notes: orderAddress?.notes ?? '',
          }}
          submitting={submittingAddress}
          onSave={onSave}
        />
      </CheckoutBlockVStack>
    );
  },
);
